import { template } from '../utils/TemplateParser';
import { lang } from '../lang';
import { vars } from '../utils/vars';
import { api } from '../utils/api';
import { formatStartDate, calculateDuration, makeEndDate } from '../utils';
import { RSVP } from '../components/RSVP';
import { DateTime } from 'luxon';
import { EventDetails } from './EventDetails';

export class SeriesOverview extends EventDetails {
    constructor({id}) {
        super({id});
        super.setProperty('id', 'series-overview-page');        // Have to set the element ID on the grandparent class
        // Don't need to fetch here, parent class constructor already calling it
    }

    fetch(id, instanceId) {
        Promise.all([
            api.getEvent({id}),
            api.getTags()
        ])
        .then(data => {
            // data[0] is event
            // data[1] is tags
            this.model = data[0];
            this.model.hasRsvpEmails = vars.pubcalendar.rsvpEmailOn && this.model.rsvpEmailOn;
            this.recurring = this.model.recurring || false;
            this.instanceDuration = calculateDuration(data[0]);
            this.rsvp = new RSVP(this.model, 'series-overview-page');
            this.controls.tagCollection = data[1];
            this.controls.render();
            this.render();
        })
        .catch((error) => {
            // redirect to calendar homepage
            this.triggerRouteChange(this.getRouteURL(''));
        } );
    }

    sidebarTemplate() {
        return template`
            <section class="sidebar">
                <div class="sidebar-background">
                    ${this.imageTemplate()}
                    <div class="details">
                        ${this.eventSummaryTemplate()}
                        ${this.organizerTemplate()}
                        ${this.locationTemplate()}
                        <div class="divider"></div>
                        ${this.rsvp}
                        ${this.controls.ticketTemplate(this.model)}
                    </div>
                    ${this.virtualEventTemplate()}
                    ${this.mapTemplate()}
                </div>
            </section>
        `;
    }

    eventSummaryTemplate() {
        // All day events have a startDate, but not startDatetime
        const startDate = this.formatDate(this.model.startDatetime || this.model.startDate).toLocaleString(DateTime.DATE_HUGE);
        const endDate = this.formatDate(this.model.untilDate).toLocaleString(DateTime.DATE_HUGE);
        return template`
            <div class="event-series">
                <div class="event-title">${lang.EventSeries}</div>
                <div class="detail-with-icon date" tabindex="0" aria-label="${lang.Begins}"><i class="icon fas fa-calendar-alt"></i><div>${lang.Begins}: ${startDate}</div></div>
                <div class="detail-with-icon date" tabindex="0" aria-label="${lang.Ends}"><i class="icon fas fa-calendar-alt"></i><div>${lang.Ends}: ${endDate}</div></div>
                <div class="detail-without-icon" tabindex="0" aria-label="${lang.Events}"><span class="icon-placeholder"></span><div>${lang.Events}: ${this.model.instances.length}</div></div>
            </div>
        `;
    }

    contentTemplate() {
        const organizer = this.model.organizer ? `${lang.OrganizedBy} ${this.model.organizer}` : '';
        return template`
            <section class="main-content-area">
                ${this.eventLabelTemplate()}
                <h1 class="event-title" id="event-title" tabindex="0">${this.model.title}</h1>
                <div class="sponsored-by" ${organizer ? `tabindex="0"` : ''}>${organizer}</div>

                ${this.imageTemplate()}
                ${this.descriptionTemplate()}
                ${this.instanceListTemplate()}
            </section>
        `;
    }

    instanceListTemplate() {
        const liMarkup = (instance) => {
            // formatStartDate function requires object with: startDate and endDate, or startDateTime and endDateTime
            const end = makeEndDate(this.model, instance, this.instanceDuration);
            let event;
            if (this.model.startDate) {
                event = {
                    startDate: instance,
                    endDate: end
                }
            }
            else if (this.model.startDatetime) {
                event = {
                    startDatetime: instance,
                    endDatetime: end
                }
            }
            return template`<li class="event-link-formatted"><a href="${this.getRouteURL(`event-details/${this.model.id}/instances/${instance}`)}">${formatStartDate(event)}</a></li>`
        };
        const list = template`
            <ul class="event-series-list">
                ${this.model.instances.map((instance) => liMarkup(instance))}
            </ul>
        `
        return list;
    }

    personalizationTracking(eventID) {
        if(!window._paq) return; // if _paq global variable does not exist it means tracking hasn't been enabled for this calendar
        window._paq.push(['setCustomUrl', `${window.location.pathname}/CALENDAR_EVENT/${eventID}/series-overview`]);
        window._paq.push(['trackPageView']);
    }
}